import { Stack } from "@mantine/core"
import { useDebouncedCallback, useDisclosure } from "@mantine/hooks"
import { InformationRequestStatus } from "@tfg/db"
import { Select, MultiSelect, LabelWithTooltip, TextInput } from "@tfg/ui"
import { useCallback, useEffect, useState } from "react"

import {
  CustomFieldConstraint,
  CustomFieldValueRequirement,
  TraceabilityResponses,
} from "@tfg/types/endpoints/traceability"
import { BatchUpdateKeysEnum } from "@tfg/types/schemas/modules/traceability/dtos/find-empty-batch-operations"

import { Locale } from "~/i18n"
import { useBatchUpdateModalContext } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/BatchModal/store/BatchModalContext.tsx"
import { BatchPopover } from "~/pages/SupplierPortal/TraceabilityRequests/components/BatchPopover.tsx"
import { CustomFieldDropzoneWrapper } from "~/pages/SupplierPortal/TraceabilityRequests/components/CustomFieldDropzoneWrapper"
import { getCustomFieldParser } from "~/pages/SupplierPortal/TraceabilityRequests/components/getCustomFieldParser.ts"
import { useCustomFieldInitialValue } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useCustomFieldInitialValue"
import {
  updateCustomFieldValue,
  useUpsertCustomFieldValueHook,
} from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useUpsertCustomFieldValue"
import { useInformationRequestContext } from "~/pages/SupplierPortal/TraceabilityRequests/store/InformationRequestContext"
import { OperationItem } from "~/pages/SupplierPortal/TraceabilityRequests/utils.ts"
import {
  getTranslation,
  useCurrentLanguage,
  useGetTranslation,
} from "~/utils/translations.ts"

type Props = {
  cfd: TraceabilityResponses.PublicCustomFieldDef
  operationItem: OperationItem
  doNotKnow: boolean
  status: InformationRequestStatus
  isLoading?: boolean
}

const getInputComponent = ({
  cfd,
  value,
  disabled,
  error,
  handleChange,
  inputOpen,
  operationItem,
  label,
  currentLanguage,
  isUpdating,
}: {
  cfd: TraceabilityResponses.PublicCustomFieldDef
  value: CustomFieldValue["value"]
  disabled: boolean
  error?: string
  handleChange: (value: string | string[] | null) => void
  inputOpen: () => void
  operationItem: OperationItem
  label: string
  setError: (error: string | undefined) => void
  currentLanguage: Locale
  isUpdating: boolean
}) => {
  const LabelComponent = () => {
    return (
      <>
        <LabelWithTooltip
          justify="space-between"
          label={label ?? ""}
          hoverHelperText={
            cfd.message ? getTranslation(cfd.message) : undefined
          }
          withAsterisk={
            cfd.valueRequirement === CustomFieldValueRequirement.REQUIRED
          }
        />
      </>
    )
  }

  if (
    cfd.constraint === CustomFieldConstraint.SELECT ||
    cfd.constraint === CustomFieldConstraint.STANDARD
  ) {
    return (
      <Select
        key={`${operationItem.nodeId}-${cfd.id}`}
        label={<LabelComponent />}
        disabled={disabled}
        clearable
        error={error}
        value={value as string}
        isLoading={isUpdating}
        onChange={(value) => {
          handleChange(value)
          inputOpen()
        }}
        data={cfd.enumValuesTranslated.map((x) => ({
          value: x.originalText,
          label: getTranslation(x, currentLanguage),
        }))}
      />
    )
  } else if (cfd.constraint === CustomFieldConstraint.MULTI_SELECT) {
    return (
      <MultiSelect
        key={`${operationItem.nodeId}-${cfd.id}`}
        label={<LabelComponent />}
        clearable
        size="xs"
        disabled={disabled}
        error={error}
        value={value == "" ? [] : (value as string[])}
        onChange={(val) => {
          handleChange(val)
          inputOpen()
        }}
        data={cfd.enumValuesTranslated.map((x) => ({
          value: x.originalText,
          label: getTranslation(x, currentLanguage),
        }))}
      />
    )
  }

  return (
    <TextInput
      key={`${operationItem.nodeId}-${cfd.id}`}
      label={<LabelComponent />}
      disabled={disabled}
      error={error}
      value={value as string}
      isLoading={isUpdating}
      labelProps={{
        w: "100%",
      }}
      onChange={(e) => {
        handleChange(e.currentTarget.value)
      }}
    />
  )
}

export type CustomFieldValue = Omit<
  TraceabilityResponses.CustomFieldValue,
  "value"
> & {
  value: string | string[]
  selectedFile: File | null
}

export function CustomFieldInput({
  cfd,
  operationItem,
  doNotKnow,
  status,
  isLoading,
}: Props) {
  const label = useGetTranslation(cfd.textContent)
  const currentLanguage = useCurrentLanguage()
  const [error, setError] = useState<string | undefined>(undefined)
  const initialValue = useCustomFieldInitialValue(cfd, setError, operationItem)
  const [value, setValue] = useState<CustomFieldValue>(initialValue)
  const enumValues = cfd.enumValuesTranslated.map((x) => x.originalText)
  const parser = getCustomFieldParser(cfd, enumValues)
  const { setBatchModalInfo, setBatchModalOpen } = useBatchUpdateModalContext()

  const { setCustomFieldValues } = useInformationRequestContext()
  useEffect(() => {
    setValue(initialValue)
  }, [initialValue, cfd.textContent])

  const { upsertCustomFieldValue, isUpdating } = useUpsertCustomFieldValueHook({
    setValue,
    setCustomFieldValues,
    cfd,
    operationItem,
  })

  const handleInput = useDebouncedCallback((newValue: string | string[]) => {
    setValue((prev) => ({ ...prev, value: newValue }))
    setError(undefined)

    const parsed = parser.safeParse(newValue)
    const parsedValue =
      newValue.length === 0 ? "" : parsed.success ? parsed.data : null
    if (parsedValue !== null) {
      setCustomFieldValues((prev) => {
        if (!operationItem.operation) {
          return prev
        }
        return updateCustomFieldValue(prev, {
          id: value.id,
          value: parsedValue ?? "",
          customFieldDefinitionId: cfd.id,
          operationId: operationItem.operation?.id,
        })
      })
      upsertCustomFieldValue({
        operationId: operationItem.operation?.id,
        customFieldDefinitionId: cfd.id,
        value: parsedValue ?? "",
        documentId: value.document ? value.document.id : null,
      })
      inputOpen()
    } else if (!disabled) {
      setError(parsed.error?.errors[0].message ?? "")
    }
  }, 800)

  function handleChange(value: string | string[] | null) {
    const newValue = value ?? ""
    setValue((prev) => ({ ...prev, value: newValue }))
    handleInput(newValue)
  }

  const disabled = doNotKnow || status !== InformationRequestStatus.OPENED

  const [inputOpened, { open: inputOpen, close: inputClose }] =
    useDisclosure(false)

  const openModal = useCallback(() => {
    if (!operationItem.operation) {
      return
    }
    setBatchModalInfo({
      activityId: operationItem.activityId,
      operationId: operationItem.operation?.id,
      requestId: operationItem.operation.informationRequestId,
      update: {
        contactId: operationItem.operation.contact.id,
        customFieldsValues: [
          {
            customFieldDefinitionId: cfd.id,
            value:
              typeof value.value === "string"
                ? value.value
                : JSON.stringify(value.value),
            documentId: value.document ? value.document.id : null,
          },
        ],
      },
      label,
      key: BatchUpdateKeysEnum.enum.customFieldsValues,
      displayActivityId: cfd.onActivityId ?? operationItem.activityId,
    })
    inputClose()
    setBatchModalOpen(true)
  }, [
    cfd,
    operationItem,
    value,
    label,
    setBatchModalInfo,
    setBatchModalOpen,
    inputClose,
  ])

  if (!operationItem.operation) {
    return null
  }

  return (
    <Stack key={`${operationItem.nodeId}-${cfd.id}`}>
      <CustomFieldDropzoneWrapper
        value={value}
        setValue={setValue}
        customFieldValueId={value?.id ?? ""}
        operationId={operationItem.operation?.id}
        constraint={cfd.constraint}
        requirementStatus={cfd.documentRequirement}
        label={label}
        displayActivityId={cfd.onActivityId ?? operationItem.activityId}
        openModal={openModal}
      >
        <BatchPopover
          target={getInputComponent({
            cfd,
            value: value?.value ?? "",
            disabled,
            error,
            handleChange,
            inputOpen,
            operationItem,
            label,
            setError,
            currentLanguage,
            isUpdating,
          })}
          onClick={openModal}
          close={inputClose}
          opened={inputOpened && !isLoading}
          type={label}
          displayActivityId={cfd.onActivityId ?? operationItem.activityId}
        />
      </CustomFieldDropzoneWrapper>
    </Stack>
  )
}
